import React, { useState, useEffect, useRef } from 'react';
import {
    Save, Share2, Download,
    Settings, ArrowLeft, AlignJustify, Table,
    Maximize, Minimize, PanelRightClose, PanelRightOpen, Loader2,
    LayoutTemplate, UploadCloud, FunctionSquare, History,
    Database, X, ChevronDown, Check, Sparkles, AlertTriangle, Palette
} from 'lucide-react';
import { COLOR_THEMES, type ColorThemeId, type LayoutId } from '@/lib/dashboardThemes';
import { apiFetch } from '@/api';
import { DataModelSelectionModal } from './DataModelSelectionModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ── Animation keyframe ────────────────────────────────────────────────────────
const FADE_IN = '@keyframes fadeSlideIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}@keyframes slideInRight{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}';

// ── Theme Panel ────────────────────────────────────────────────────────────────
const ThemePanel = ({ currentColorTheme, currentLayout, onColorThemeChange, onLayoutChange, onClose }: {
    currentColorTheme: ColorThemeId;
    currentLayout: LayoutId;
    onColorThemeChange: (id: ColorThemeId) => void;
    onLayoutChange: (id: LayoutId) => void;
    onClose: () => void;
}) => {
    const colorThemes = Object.values(COLOR_THEMES) as any[];

    const LAYOUTS = [
        { id: 'flat', name: 'Flat' },
        { id: 'border', name: 'Border' },
        { id: 'top_border', name: 'Top Border' },
        { id: 'fill_color', name: 'Fill Color' },
        { id: 'top_border_multicolor', name: 'Top Border Multicolor' },
        { id: 'fill_multicolor', name: 'Fill Multicolor' }
    ];

    const renderLayoutPreview = (id: string) => {
        const isFlat = id === 'flat';
        const isBorder = id === 'border';
        const isTopBorder = id === 'top_border';
        const isFillColor = id === 'fill_color';
        const isTopBorderMulti = id === 'top_border_multicolor';
        const isFillMulti = id === 'fill_multicolor';

        const multiColors = ['#14b8a6', '#3b82f6', '#f59e0b', '#ef4444', '#ec4899']; 
        const defaultColor = '#3b82f6'; 

        const getCardStyle = (index: number) => {
            let baseStyle: React.CSSProperties = {
                background: '#fff',
                borderRadius: 4,
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                boxShadow: isFlat ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                border: isFlat ? 'none' : '1px solid #e2e8f0',
            };

            if (isTopBorder) {
                baseStyle.borderTop = `2px solid ${defaultColor}`;
            } else if (isTopBorderMulti) {
                baseStyle.borderTop = `2px solid ${multiColors[index]}`;
            }

            return baseStyle;
        };

        const getHeaderStyle = (index: number) => {
            let hStyle: React.CSSProperties = {
                height: 8,
                background: 'transparent',
                display: 'flex', alignItems: 'center', padding: '0 3px'
            };
            if (isFillColor) {
                hStyle.background = defaultColor;
            } else if (isFillMulti) {
                hStyle.background = multiColors[index];
            }
            return hStyle;
        };

        const getHeaderLineStyle = (index: number) => {
             return {
                 height: 2,
                 width: 8,
                 background: (isFillColor || isFillMulti) ? '#fff' : '#cbd5e1',
                 borderRadius: 1
             };
        };

        return (
            <div style={{
                height: 56,
                background: '#f3e8ff',
                borderRadius: 6,
                padding: 5,
                display: 'flex',
                flexDirection: 'column',
                gap: 3
            }}>
                {/* Top row */}
                <div style={{ display: 'flex', gap: 3, height: '50%' }}>
                    {[0, 1, 2].map(i => (
                        <div key={i} style={{ ...getCardStyle(i), flex: 1 }}>
                            <div style={getHeaderStyle(i)}>
                                <div style={getHeaderLineStyle(i)} />
                            </div>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 2, gap: 1 }}>
                                 {i === 1 ? ( // Pie chart mock
                                     <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'conic-gradient(#3b82f6 0 60%, #f43f5e 60% 100%)' }} />
                                 ) : ( // Bar chart mock
                                     <>
                                         <div style={{ width: 2.5, height: '40%', background: '#f59e0b', borderRadius: '1px 1px 0 0' }} />
                                         <div style={{ width: 2.5, height: '80%', background: '#3b82f6', borderRadius: '1px 1px 0 0' }} />
                                         <div style={{ width: 2.5, height: '60%', background: '#10b981', borderRadius: '1px 1px 0 0' }} />
                                     </>
                                 )}
                            </div>
                        </div>
                    ))}
                </div>
                {/* Bottom row */}
                <div style={{ display: 'flex', gap: 3, height: '50%' }}>
                    {[3, 4].map(i => (
                        <div key={i} style={{ ...getCardStyle(i), flex: 1 }}>
                            <div style={getHeaderStyle(i)}>
                                <div style={getHeaderLineStyle(i)} />
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 3px', gap: 1 }}>
                                 <div style={{ height: 1.5, background: '#e2e8f0', width: '100%', borderRadius: 1 }} />
                                 <div style={{ height: 1.5, background: '#e2e8f0', width: '80%', borderRadius: 1 }} />
                                 <div style={{ height: 1.5, background: '#e2e8f0', width: '90%', borderRadius: 1 }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: 300,
                background: '#ffffff', borderLeft: '1px solid #e5e7eb',
                boxShadow: '-8px 0 24px rgba(0,0,0,0.10)',
                zIndex: 9999, display: 'flex', flexDirection: 'column',
                animation: 'slideInRight 0.2s ease',
                fontFamily: "'Segoe UI', system-ui, sans-serif",
            }}
        >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Palette style={{ width: 16, height: 16, color: '#6366f1' }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Dashboard Themes</span>
                </div>
                <button onClick={onClose} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <X style={{ width: 14, height: 14 }} />
                </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {/* Color Theme list */}
                <div style={{ padding: '16px 16px 0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {colorThemes.map((t) => {
                            const active = currentColorTheme === t.id;
                            return (
                                <button key={t.id} type="button" onClick={() => onColorThemeChange(t.id as ColorThemeId)}
                                    style={{
                                        border: active ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                                        borderRadius: 12, overflow: 'hidden', background: 'transparent',
                                        cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s',
                                        boxShadow: active ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                                    }}
                                    onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = '#93c5fd'; }}
                                    onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = '#e5e7eb'; }}
                                >
                                    {/* Preview bar */}
                                    <div style={{ height: 64, background: t.bg, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px' }}>
                                        {/* KPI mock */}
                                        <div style={{
                                            height: 32, width: 72, borderRadius: 8,
                                            background: t.widgetBg || '#fff',
                                            border: `1px solid ${t.widgetBorder}`,
                                            boxShadow: t.id === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 9, fontWeight: 700,
                                            color: t.widgetText || '#252423',
                                        }}>42.5K</div>
                                        {/* Bar chart mock */}
                                        <div style={{
                                            flex: 1, height: 44, borderRadius: 8,
                                            background: t.widgetBg || '#fff',
                                            border: `1px solid ${t.widgetBorder}`,
                                            display: 'flex', alignItems: 'flex-end', gap: 2, padding: '6px 6px 4px',
                                        }}>
                                            {[55, 80, 40, 70, 50, 90].map((h, i) => (
                                                <div key={i} style={{
                                                    flex: 1, borderRadius: '2px 2px 0 0',
                                                    height: `${h}%`,
                                                    background: t.colors[i % t.colors.length],
                                                    opacity: 0.9,
                                                }} />
                                            ))}
                                        </div>
                                        {/* Color dots */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            {t.colors.slice(0, 4).map((c: string, i: number) => (
                                                <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
                                            ))}
                                        </div>
                                    </div>
                                    {/* Info row */}
                                    <div style={{
                                        padding: '10px 14px',
                                        background: active ? '#eff6ff' : '#fff',
                                        borderTop: `1px solid ${active ? '#bfdbfe' : '#f3f4f6'}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.accent, flexShrink: 0 }} />
                                            <span style={{ fontSize: 13, fontWeight: 700, color: active ? '#1d4ed8' : '#111827' }}>{t.name}</span>
                                        </div>
                                        {active && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: '#2563eb', background: '#dbeafe', padding: '2px 8px', borderRadius: 999 }}>
                                                <Check style={{ width: 10, height: 10 }} /> Active
                                            </span>
                                        )}
                                    </div>
                                    {/* Description */}
                                    <div style={{ padding: '0 14px 10px', background: active ? '#eff6ff' : '#fff' }}>
                                        <p style={{ fontSize: 11, color: '#6b7280', margin: 0, lineHeight: 1.5 }}>{t.description}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Layouts list */}
                <div style={{ padding: '24px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Layouts:</span>
                        <button style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Customize</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {LAYOUTS.map((lyt) => {
                            const active = currentLayout === lyt.id;
                            return (
                                <div key={lyt.id} onClick={() => onLayoutChange(lyt.id as LayoutId)} style={{ 
                                    cursor: 'pointer',
                                    background: active ? '#eff6ff' : 'transparent',
                                    borderRadius: 8,
                                    border: active ? '1px solid #3b82f6' : '1px solid transparent',
                                    padding: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxSizing: 'border-box'
                                }}>
                                    <div style={{ width: '100%' }}>
                                        {renderLayoutPreview(lyt.id)}
                                    </div>
                                    <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 8 }}>
                                        {lyt.name}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};;

// ── Role → color map for preview blocks ──────────────────────────────────────
const ROLE_STYLE: Record<string, { bg: string; border: string; text: string }> = {
    kpi:     { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
    bar:     { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
    line:    { bg: '#dcfce7', border: '#86efac', text: '#14532d' },
    area:    { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
    donut:   { bg: '#fef3c7', border: '#fcd34d', text: '#78350f' },
    pie:     { bg: '#ffedd5', border: '#fdba74', text: '#7c2d12' },
    table:   { bg: '#ede9fe', border: '#c4b5fd', text: '#4c1d95' },
    scatter: { bg: '#fdf4ff', border: '#e879f9', text: '#701a75' },
    gauge:   { bg: '#fff1f2', border: '#fda4af', text: '#881337' },
    filter:  { bg: '#f0fdf4', border: '#86efac', text: '#166534' },
    hbar:    { bg: '#ecfdf5', border: '#6ee7b7', text: '#065f46' },
};

// ══════════════════════════════════════════════════════════════════════════════
//  DASHBOARD TEMPLATES  (GRID_W = 120 cols, GRID_H varies per template)
// ══════════════════════════════════════════════════════════════════════════════
export const DASHBOARD_TEMPLATES = [

    // ── 1 ─ Executive KPI Overview ───────────────────────────────────────────
    {
        id: 'executive-kpi',
        name: 'Executive KPI Overview',
        description: '4 KPI scorecards across the top with a monthly trend bar and growth line chart below — ideal for C-suite views.',
        icon: '📊', category: 'Leadership', tags: ['KPI','Bar','Line'],
        gridH: 68,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',  label:'Revenue',      x:0,  y:12,  w:30, h:18 },
            { role:'kpi',  label:'Customers',    x:30, y:12,  w:30, h:18 },
            { role:'kpi',  label:'Avg Order',    x:60, y:12,  w:30, h:18 },
            { role:'kpi',  label:'Growth %',     x:90, y:12,  w:30, h:18 },
            { role:'bar',  label:'Monthly Rev',  x:0,  y:30, w:60, h:38 },
            { role:'line', label:'Growth Trend', x:60, y:30, w:60, h:38 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',  title:'Total Revenue',   layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',  title:'Total Customers', layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',  title:'Avg Order Value', layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',  title:'Growth Rate',     layout:{x:90, y:12,  w:30, h:18} },
            { type:'bar',  title:'Monthly Revenue', layout:{x:0,  y:30, w:60, h:38} },
            { type:'line', title:'Trend Over Time', layout:{x:60, y:30, w:60, h:38} },
        ],
    },

    // ── 2 ─ Sales Analytics Board ─────────────────────────────────────────────
    {
        id: 'sales-analytics',
        name: 'Sales Analytics Board',
        description: '3 KPIs, ranked bar, category donut, area trend, and a full-width transaction table at the bottom.',
        icon: '📈', category: 'Sales', tags: ['KPI','Donut','Table'],
        gridH: 98,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Sales',        x:0,  y:12,  w:40, h:18 },
            { role:'kpi',   label:'Units',        x:40, y:12,  w:40, h:18 },
            { role:'kpi',   label:'Conv Rate',    x:80, y:12,  w:40, h:18 },
            { role:'bar',   label:'Top Products', x:0,  y:30, w:45, h:38 },
            { role:'donut', label:'By Category',  x:45, y:30, w:33, h:38 },
            { role:'area',  label:'Sales Trend',  x:78, y:30, w:42, h:38 },
            { role:'table', label:'Transactions', x:0,  y:68, w:120,h:30 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Sales',         layout:{x:0,  y:12,  w:40, h:18} },
            { type:'kpi',   title:'Units Sold',          layout:{x:40, y:12,  w:40, h:18} },
            { type:'kpi',   title:'Conversion Rate',     layout:{x:80, y:12,  w:40, h:18} },
            { type:'bar',   title:'Top Products',        layout:{x:0,  y:30, w:45, h:38} },
            { type:'donut', title:'Sales by Category',   layout:{x:45, y:30, w:33, h:38} },
            { type:'area',  title:'Sales Over Time',     layout:{x:78, y:30, w:42, h:38} },
            { type:'table', title:'Transaction Details', layout:{x:0,  y:68, w:120,h:30} },
        ],
    },

    // ── 3 ─ Billing Anomaly Monitor ───────────────────────────────────────────
    {
        id: 'billing-anomaly',
        name: 'Billing Anomaly Monitor',
        description: '4 anomaly KPIs, dual deviation line + bar charts, a distribution donut, and an anomaly detail table.',
        icon: '🔍', category: 'Healthcare', tags: ['Anomaly','Donut','Table'],
        gridH: 104,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Records',      x:0,  y:12,  w:30, h:18 },
            { role:'kpi',   label:'Anomalies',    x:30, y:12,  w:30, h:18 },
            { role:'kpi',   label:'At Risk',      x:60, y:12,  w:30, h:18 },
            { role:'kpi',   label:'Undercharge',  x:90, y:12,  w:30, h:18 },
            { role:'line',  label:'Diag Codes',   x:0,  y:30, w:60, h:38 },
            { role:'bar',   label:'Anomaly Bar',  x:60, y:30, w:60, h:38 },
            { role:'donut', label:'Distribution', x:0,  y:68, w:42, h:36 },
            { role:'table', label:'Anomaly Data', x:42, y:68, w:78, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Records',              layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',   title:'Total Anomalies Detected',   layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',   title:'Charges at Risk',            layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',   title:'Undercharge',                layout:{x:90, y:12,  w:30, h:18} },
            { type:'line',  title:'Diagnosis Code Deviations',  layout:{x:0,  y:30, w:60, h:38} },
            { type:'bar',   title:'Anomaly Bar Chart',          layout:{x:60, y:30, w:60, h:38} },
            { type:'donut', title:'Billing Distribution',       layout:{x:0,  y:68, w:42, h:36} },
            { type:'table', title:'Anomaly Detail Data',        layout:{x:42, y:68, w:78, h:36} },
        ],
    },

    // ── 4 ─ Appointment Summary ───────────────────────────────────────────────
    {
        id: 'appointment-summary',
        name: 'Appointment Summary',
        description: '5 appointment KPIs, 2 patient-type donuts, appointment status table, and dual area charts for growth & fill rate.',
        icon: '🏥', category: 'Healthcare', tags: ['KPI','Donut','Area'],
        gridH: 102,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Appts',         x:0,  y:12,  w:24, h:18 },
            { role:'kpi',   label:'Cancels',       x:24, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Cancel %',      x:48, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Slot Fill',     x:72, y:12,  w:24, h:18 },
            { role:'kpi',   label:'No-Show',       x:96, y:12,  w:24, h:18 },
            { role:'donut', label:'New vs Recur',  x:0,  y:30, w:28, h:36 },
            { role:'donut', label:'Walk-in/Sched', x:28, y:30, w:28, h:36 },
            { role:'table', label:'Encounters',    x:56, y:30, w:64, h:36 },
            { role:'area',  label:'Growth Trend',  x:0,  y:66, w:60, h:36 },
            { role:'area',  label:'Fill Rate',     x:60, y:66, w:60, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'No. of Appointments',        layout:{x:0,  y:12,  w:24, h:18} },
            { type:'kpi',   title:'Cancellation Count',         layout:{x:24, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Cancellation Rate %',        layout:{x:48, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Slot Fill Rate %',           layout:{x:72, y:12,  w:24, h:18} },
            { type:'kpi',   title:'No Show Rate %',             layout:{x:96, y:12,  w:24, h:18} },
            { type:'donut', title:'New vs Recurring Patients',  layout:{x:0,  y:30, w:28, h:36} },
            { type:'donut', title:'Walk-ins vs Scheduled',      layout:{x:28, y:30, w:28, h:36} },
            { type:'table', title:'Encounters by Appt Status',  layout:{x:56, y:30, w:64, h:36} },
            { type:'area',  title:'Growth Trend',               layout:{x:0,  y:66, w:60, h:36} },
            { type:'area',  title:'Fill Rate',                  layout:{x:60, y:66, w:60, h:36} },
        ],
    },

    // ── 5 ─ Charges Summary ───────────────────────────────────────────────────
    {
        id: 'charges-summary',
        name: 'Charges Summary',
        description: '5 charge KPIs, service-line horizontal bar, providers bar, top insurance bar, and CPT code ranking table.',
        icon: '💰', category: 'Finance', tags: ['Charges','Bar','Table'],
        gridH: 104,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Charges',       x:0,  y:12,  w:24, h:18 },
            { role:'kpi',   label:'Allowed',       x:24, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Adjustment',    x:48, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Owed',          x:72, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Charge Lag',    x:96, y:12,  w:24, h:18 },
            { role:'bar',   label:'Service Line',  x:0,  y:30, w:60, h:38 },
            { role:'bar',   label:'Top Providers', x:60, y:30, w:60, h:38 },
            { role:'bar',   label:'Top Insurance', x:0,  y:68, w:60, h:36 },
            { role:'table', label:'Top CPTs',      x:60, y:68, w:60, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Charges',          layout:{x:0,  y:12,  w:24, h:18} },
            { type:'kpi',   title:'Allowed Amount',         layout:{x:24, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Adjustment Amount',      layout:{x:48, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Total Owed Amount',      layout:{x:72, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Charge Lag',             layout:{x:96, y:12,  w:24, h:18} },
            { type:'bar',   title:'Service Line by Charges',layout:{x:0,  y:30, w:60, h:38} },
            { type:'bar',   title:'Top Providers by Charges',layout:{x:60,y:30, w:60, h:38} },
            { type:'bar',   title:'Top 10 Insurance',       layout:{x:0,  y:68, w:60, h:36} },
            { type:'table', title:'Top 10 CPT Codes',       layout:{x:60, y:68, w:60, h:36} },
        ],
    },

    // ── 6 ─ Payment Analytics ─────────────────────────────────────────────────
    {
        id: 'payment-analytics',
        name: 'Payment Analytics',
        description: '6 payment KPIs in 2 rows, insurance vs patient donut, payer bar, service-line bar, procedure donut, and MoM trend.',
        icon: '💳', category: 'Finance', tags: ['Payments','Donut','MoM'],
        gridH: 122,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Charge',       x:0,  y:12,  w:40, h:16 },
            { role:'kpi',   label:'Payment',      x:40, y:12,  w:40, h:16 },
            { role:'kpi',   label:'Total AR',     x:80, y:12,  w:40, h:16 },
            { role:'kpi',   label:'Collection%',  x:0,  y:28, w:40, h:16 },
            { role:'kpi',   label:'Adj %',        x:40, y:28, w:40, h:16 },
            { role:'kpi',   label:'Days Collect', x:80, y:28, w:40, h:16 },
            { role:'donut', label:'Ins vs Pat',   x:0,  y:44, w:24, h:38 },
            { role:'bar',   label:'By Payer',     x:24, y:44, w:48, h:38 },
            { role:'bar',   label:'By Svc Line',  x:72, y:44, w:48, h:38 },
            { role:'donut', label:'By Procedure', x:0,  y:82, w:28, h:40 },
            { role:'bar',   label:'MoM Trend',    x:28, y:82, w:92, h:40 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Charge',           layout:{x:0,  y:12,  w:40, h:16} },
            { type:'kpi',   title:'Total Payment',          layout:{x:40, y:12,  w:40, h:16} },
            { type:'kpi',   title:'Total AR',               layout:{x:80, y:12,  w:40, h:16} },
            { type:'kpi',   title:'Collection Rate %',      layout:{x:0,  y:28, w:40, h:16} },
            { type:'kpi',   title:'Adjustment Percentage',  layout:{x:40, y:28, w:40, h:16} },
            { type:'kpi',   title:'Days to Collect',        layout:{x:80, y:28, w:40, h:16} },
            { type:'donut', title:'Insurance vs Patient',   layout:{x:0,  y:44, w:24, h:38} },
            { type:'bar',   title:'Payments by Payer',      layout:{x:24, y:44, w:48, h:38} },
            { type:'bar',   title:'Payment by Service Line',layout:{x:72, y:44, w:48, h:38} },
            { type:'donut', title:'Pay by Procedure Group', layout:{x:0,  y:82, w:28, h:40} },
            { type:'bar',   title:'Month over Month Trend', layout:{x:28, y:82, w:92, h:40} },
        ],
    },

    // ── 7 ─ Operations Monitor ─────────────────────────────────────────────────
    {
        id: 'operations-monitor',
        name: 'Operations Monitor',
        description: '4 SLA/uptime KPIs, 3 gauge meters, combined trend line, and a live incident log table.',
        icon: '⚙️', category: 'Operations', tags: ['Gauge','Line','Table'],
        gridH: 94,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'SLA %',       x:0,  y:12,  w:30, h:18 },
            { role:'kpi',   label:'Throughput',  x:30, y:12,  w:30, h:18 },
            { role:'kpi',   label:'Error Rate',  x:60, y:12,  w:30, h:18 },
            { role:'kpi',   label:'Uptime',      x:90, y:12,  w:30, h:18 },
            { role:'gauge', label:'CPU',         x:0,  y:30, w:40, h:28 },
            { role:'gauge', label:'Memory',      x:40, y:30, w:40, h:28 },
            { role:'gauge', label:'Disk I/O',    x:80, y:30, w:40, h:28 },
            { role:'line',  label:'Sys Trend',   x:0,  y:58, w:80, h:36 },
            { role:'table', label:'Incidents',   x:80, y:58, w:40, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'SLA Compliance %',  layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',   title:'Throughput',        layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',   title:'Error Rate',        layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',   title:'System Uptime',     layout:{x:90, y:12,  w:30, h:18} },
            { type:'gauge', title:'CPU Utilization',   layout:{x:0,  y:30, w:40, h:28} },
            { type:'gauge', title:'Memory Usage',      layout:{x:40, y:30, w:40, h:28} },
            { type:'gauge', title:'Disk I/O',          layout:{x:80, y:30, w:40, h:28} },
            { type:'line',  title:'System Trend',      layout:{x:0,  y:58, w:80, h:36} },
            { type:'table', title:'Incident Log',      layout:{x:80, y:58, w:40, h:36} },
        ],
    },

    // ── 8 ─ Marketing Campaign ─────────────────────────────────────────────────
    {
        id: 'marketing-campaign',
        name: 'Marketing Campaign',
        description: '4 campaign KPIs, channel spend bar, conversion donut, audience scatter, and a full-width engagement trend.',
        icon: '🎯', category: 'Marketing', tags: ['Campaign','Scatter','Donut'],
        gridH: 110,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',     label:'Impressions',  x:0,  y:12,  w:30, h:18 },
            { role:'kpi',     label:'Clicks',       x:30, y:12,  w:30, h:18 },
            { role:'kpi',     label:'Conversions',  x:60, y:12,  w:30, h:18 },
            { role:'kpi',     label:'ROAS',         x:90, y:12,  w:30, h:18 },
            { role:'bar',     label:'By Channel',   x:0,  y:30, w:45, h:48 },
            { role:'donut',   label:'Conv Mix',     x:45, y:30, w:35, h:48 },
            { role:'scatter', label:'Audience',     x:80, y:30, w:40, h:48 },
            { role:'line',    label:'Engagement',   x:0,  y:78, w:120,h:32 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',     title:'Total Impressions',  layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',     title:'Total Clicks',       layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',     title:'Conversions',        layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',     title:'Return on Ad Spend', layout:{x:90, y:12,  w:30, h:18} },
            { type:'bar',     title:'Spend by Channel',   layout:{x:0,  y:30, w:45, h:48} },
            { type:'donut',   title:'Conversion Mix',     layout:{x:45, y:30, w:35, h:48} },
            { type:'scatter', title:'Audience Segments',  layout:{x:80, y:30, w:40, h:48} },
            { type:'line',    title:'Engagement Over Time',layout:{x:0, y:78, w:120,h:32} },
        ],
    },

];

// ── Grid preview renderer (proportional aspect from gridH) ────────────────────
const GRID_W = 120;

const TemplateGridPreview = ({ blocks, gridH = 90 }: { blocks: any[]; gridH: number }) => {
    // Compute aspect ratio: height/width * 100 → paddingTop trick
    const aspectPct = `${Math.min(Math.round((gridH / GRID_W) * 100), 95)}%`;
    return (
        <div className="relative w-full rounded-lg border border-[#e2e8f0] dark:border-slate-700 overflow-hidden bg-[#f1f5f9] dark:bg-slate-900/40"
            style={{ paddingTop: aspectPct }}>
            <div className="absolute inset-1">
                {blocks.map((b, i) => {
                    const s = ROLE_STYLE[b.role] || { bg: '#e2e8f0', border: '#94a3b8', text: '#475569' };
                    return (
                        <div key={i}
                            className="absolute flex items-center justify-center overflow-hidden"
                            style={{
                                left:   `${(b.x / GRID_W) * 100}%`,
                                top:    `${(b.y / gridH) * 100}%`,
                                width:  `${(b.w / GRID_W) * 100}%`,
                                height: `${(b.h / gridH) * 100}%`,
                                backgroundColor: s.bg,
                                borderRadius: 3,
                                border: `1px solid ${s.border}`,
                                padding: '1px 2px',
                            }}>
                            <span style={{ color: s.text, fontSize: 5, fontWeight: 700, lineHeight: 1.1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                {b.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Discard Confirmation ───────────────────────────────────────────────────────
const DiscardConfirmModal = ({ template, onConfirm, onCancel }: { template: any; onConfirm: () => void; onCancel: () => void }) => (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
        <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            style={{ animation: 'fadeSlideIn 0.18s ease' }}>
            <style>{FADE_IN}</style>

            <div className="flex items-start gap-4 px-6 pt-6 pb-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                    <h2 className="text-[15px] font-bold text-[#252423] dark:text-slate-100">Replace Existing Widgets?</h2>
                    <p className="text-[12px] text-[#605e5c] dark:text-slate-400 mt-1 leading-relaxed">
                        Applying <span className="font-semibold text-[#252423] dark:text-slate-200">"{template.name}"</span> will 
                        permanently remove all current widgets. This cannot be undone.
                    </p>
                </div>
            </div>

            {/* Mini preview */}
            <div className="mx-6 mb-4 rounded-xl border border-[#e2e8f0] dark:border-slate-700 overflow-hidden bg-[#fafafa] dark:bg-slate-900/30 p-3">
                <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-lg leading-none">{template.icon}</span>
                    <span className="text-[12px] font-semibold text-[#252423] dark:text-slate-100 flex-1">{template.name}</span>
                    <span className="text-[10px] text-[#118DF0] font-medium">{template.widgets.length} widgets</span>
                </div>
                <TemplateGridPreview blocks={template.preview} gridH={template.gridH || 90} />
            </div>

            <div className="flex items-center gap-2.5 px-6 pb-6">
                <button type="button" onClick={onCancel}
                    className="flex-1 py-2.5 text-[12px] font-semibold rounded-xl border border-[#d6d6d6] dark:border-slate-600 text-[#605e5c] dark:text-slate-300 hover:bg-[#f0f0f0] dark:hover:bg-slate-700 transition-colors">
                    Keep Current Widgets
                </button>
                <button type="button" onClick={onConfirm}
                    className="flex-1 py-2.5 text-[12px] font-semibold rounded-xl bg-amber-500 hover:bg-amber-600 text-white shadow-sm transition-colors">
                    Discard &amp; Apply Template
                </button>
            </div>
        </div>
    </div>
);

// ── Category chip colors ──────────────────────────────────────────────────────
const CATEGORY_STYLE: Record<string, string> = {
    Leadership:  'bg-purple-50 text-purple-700 border-purple-100 dark:bg-purple-900/20 dark:text-purple-300',
    Sales:       'bg-green-50 text-green-700 border-green-100 dark:bg-green-900/20 dark:text-green-300',
    Healthcare:  'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/20 dark:text-rose-300',
    Finance:     'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300',
    Operations:  'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300',
    Marketing:   'bg-orange-50 text-orange-700 border-orange-100 dark:bg-orange-900/20 dark:text-orange-300',
};

// ── Full Templates Modal ──────────────────────────────────────────────────────
const TemplatesModal = ({ onClose, onApply, existingWidgetCount = 0 }: { onClose: () => void; onApply: (t: any) => void; existingWidgetCount?: number }) => {
    const [hovered, setHovered] = useState<string | null>(null);
    const [pending, setPending] = useState<any>(null);
    const [filter, setFilter] = useState<string>('All');

    const categories = ['All', ...Array.from(new Set(DASHBOARD_TEMPLATES.map(t => t.category)))];

    const filtered = filter === 'All'
        ? DASHBOARD_TEMPLATES
        : DASHBOARD_TEMPLATES.filter(t => t.category === filter);

    const handleApply = (tpl: any) => {
        if (existingWidgetCount > 0) { setPending(tpl); }
        else { onApply(tpl); onClose(); }
    };

    return (
        <>
            <div className="fixed inset-0 z-[500] flex items-center justify-center" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
                <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-[0_25px_60px_-10px_rgba(0,0,0,0.35)] w-full mx-5 flex flex-col overflow-hidden"
                    style={{ maxWidth: 1160, maxHeight: '90vh', animation: 'fadeSlideIn 0.18s ease' }}>
                    <style>{FADE_IN}</style>

                    {/* ── Modal header ── */}
                    <div className="flex items-center gap-4 px-7 py-5 border-b border-[#e2e8f0] dark:border-slate-700 bg-gradient-to-r from-[#f8faff] to-[#f3f6ff] dark:from-slate-800 dark:to-slate-800 flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-md">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-[16px] font-bold text-[#252423] dark:text-slate-100">Dashboard Templates</h2>
                            <p className="text-[11px] text-[#a19f9d] dark:text-slate-400 mt-0.5">
                                {DASHBOARD_TEMPLATES.length} pre-built layouts — choose one to instantly populate the canvas
                            </p>
                        </div>
                        {/* Category filter pills */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {categories.map(cat => (
                                <button key={cat} type="button" onClick={() => setFilter(cat)}
                                    className={`px-3 py-1 text-[11px] font-semibold rounded-full border transition-all whitespace-nowrap ${filter === cat
                                        ? 'bg-[#118DF0] border-[#118DF0] text-white shadow-sm'
                                        : 'bg-white dark:bg-slate-800 border-[#d6d6d6] dark:border-slate-600 text-[#605e5c] dark:text-slate-400 hover:border-[#118DF0] hover:text-[#118DF0]'
                                    }`}>
                                    {cat}
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#e1dfdd] dark:hover:bg-slate-700 text-[#a19f9d] transition-colors flex-shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* ── Template grid ── */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                            {filtered.map(tpl => {
                                const isHov = hovered === tpl.id;
                                const catStyle = CATEGORY_STYLE[tpl.category] || 'bg-slate-100 text-slate-600';
                                return (
                                    <div key={tpl.id}
                                        onMouseEnter={() => setHovered(tpl.id)}
                                        onMouseLeave={() => setHovered(null)}
                                        className={`group flex flex-col rounded-2xl border-2 overflow-hidden cursor-default transition-all duration-200 ${isHov
                                            ? 'border-[#118DF0] shadow-xl shadow-blue-100/60 dark:shadow-blue-900/20 -translate-y-0.5'
                                            : 'border-[#e2e8f0] dark:border-slate-700 shadow-sm hover:shadow-md'
                                        }`}>

                                        {/* Preview area */}
                                        <div className={`p-3.5 transition-colors ${isHov ? 'bg-blue-50/60 dark:bg-blue-900/10' : 'bg-[#f8fafc] dark:bg-slate-800/40'}`}>
                                            <TemplateGridPreview blocks={tpl.preview} gridH={tpl.gridH} />
                                        </div>

                                        {/* Info row */}
                                        <div className="flex items-start gap-3 px-4 py-3.5 bg-white dark:bg-slate-800 flex-1">
                                            <span className="text-2xl leading-none mt-0.5 flex-shrink-0">{tpl.icon}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <span className="text-[13px] font-bold text-[#252423] dark:text-slate-100 leading-tight">{tpl.name}</span>
                                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border leading-none ${catStyle}`}>{tpl.category}</span>
                                                </div>
                                                <p className="text-[11px] text-[#7a7978] dark:text-slate-400 leading-snug line-clamp-2">{tpl.description}</p>
                                                {/* Tags */}
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    {tpl.tags.map((tag: string) => (
                                                        <span key={tag} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#f0f0f0] dark:bg-slate-700 text-[#605e5c] dark:text-slate-400 uppercase tracking-wide">{tag}</span>
                                                    ))}
                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 uppercase tracking-wide">{tpl.widgets.length}W</span>
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => handleApply(tpl)}
                                                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold rounded-xl transition-all shadow-sm ${isHov
                                                    ? 'bg-[#118DF0] text-white shadow-blue-200 dark:shadow-blue-900'
                                                    : 'bg-[#f0f0f0] dark:bg-slate-700 text-[#252423] dark:text-slate-200 hover:bg-[#118DF0] hover:text-white'
                                                }`}>
                                                <Sparkles className="w-3.5 h-3.5" /> Apply
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-center text-[11px] text-[#a19f9d] dark:text-slate-500 mt-6 pb-1">
                            Templates add empty widgets at precise positions. Connect your data model to populate them.
                        </p>
                    </div>
                </div>
            </div>

            {pending && (
                <DiscardConfirmModal
                    template={pending}
                    onConfirm={() => { onApply(pending); setPending(null); onClose(); }}
                    onCancel={() => setPending(null)}
                />
            )}
        </>
    );
};

// ── Small reusable header button ──────────────────────────────────────────────
const HeaderButton = ({ icon: Icon, label, onClick, variant = 'secondary', disabled = false, className = '', title = '' }: any) => {
    const base = "flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all select-none h-8 border-0";
    const variants: any = {
        primary:   "bg-[#118DF0] hover:bg-[#0070c8] text-white",
        secondary: "bg-transparent text-[#252423] dark:text-slate-300 hover:bg-[#e1dfdd] dark:hover:bg-slate-700",
        ghost:     "bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700",
    };
    return (
        <button type="button" onClick={onClick} disabled={disabled}
            className={`${base} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            title={title || label}>
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label && <span>{label}</span>}
        </button>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
//  DASHBOARD HEADER
// ══════════════════════════════════════════════════════════════════════════════
export const DashboardHeader = ({
    title = "New Dashboard",
    onAction, onTitleChange, onToggleCompact,
    selectedDataset, onPreviewData,
    isFullScreen, isSidebarOpen, onToggleSidebar,
    onSave, onPublish, onHeaderSettings, onBack,
    isSaving = false,
    titleStyle = {} as any,
    activeVersion = null as string | null,
    dataModel = null as any,
    onDataModelChange = null as any,
    isDataModelLocked = false,
    onPreviewTable = null as any,
    initialDataOpen = false,
    onTemplateApply = null as any,
    widgetCount = 0,
    isNewDashboard = false,
    currentColorTheme = 'light' as ColorThemeId,
    currentLayout = 'flat' as LayoutId,
    onColorThemeChange = null as any,
    onLayoutChange = null as any,
    isViewOnly = false,
}: any) => {
    // In view-only mode the full toolbar is hidden; the parent renders a slim view bar.
    if (isViewOnly) return null;
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [tempTitle, setTempTitle] = useState(title);
    const [isCompact, setIsCompact] = useState(false);

    // Data popup
    const [isDataOpen, setIsDataOpen] = useState(initialDataOpen);
    const dataPopupRef = useRef<HTMLDivElement>(null);
    const [allModels, setAllModels] = useState<any[]>([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [modelSearch, setModelSearch] = useState('');
    const [isChangingModel, setIsChangingModel] = useState(false);

    // Templates modal
    const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
    // Theme panel
    const [isThemePanelOpen, setIsThemePanelOpen] = useState(false);

    useEffect(() => { setTempTitle(title); }, [title]);
    useEffect(() => { if (initialDataOpen) setIsDataOpen(true); }, [initialDataOpen]);

    const hasAutoOpenedData = useRef(false);
    useEffect(() => {
        if (isNewDashboard && !dataModel && !hasAutoOpenedData.current) {
            setIsDataOpen(true);
            hasAutoOpenedData.current = true;
        }
    }, [isNewDashboard, dataModel]);

    // Fetch models when popup opens
    useEffect(() => {
        if (!isDataOpen) return;
        setModelsLoading(true);
        apiFetch('/datamodels')
            .then((d: any) => setAllModels(Array.isArray(d) ? d : (d?.items || d?.data || [])))
            .catch(() => {})
            .finally(() => setModelsLoading(false));
    }, [isDataOpen]);

    // Close data popup on outside click
    useEffect(() => {
        if (!isDataOpen) return;
        const h = (e: MouseEvent) => {
            if (dataPopupRef.current && !dataPopupRef.current.contains(e.target as Node))
                setIsDataOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [isDataOpen]);

    const handleTitleSubmit = () => {
        setIsEditingTitle(false);
        if (tempTitle.trim()) onTitleChange?.(tempTitle);
        else setTempTitle(title);
    };

    const handleModelSelect = (id: string) => {
        apiFetch(`/datamodels/${id}`)
            .then((d: any) => { onDataModelChange?.(d); setIsChangingModel(false); })
            .catch(() => { const m = allModels.find((x: any) => String(x.id) === id); if (m) { onDataModelChange?.(m); setIsChangingModel(false); } });
    };

    const filteredModels = allModels.filter((m: any) =>
        !modelSearch || m.name?.toLowerCase().includes(modelSearch.toLowerCase())
    );

    const tables: any[] = dataModel?.tables?.length ? dataModel.tables
        : (dataModel?.model_definition?.tables || []);

    return (
        <>
            <div className="h-14 bg-[#f3f3f3] dark:bg-slate-800 border-b border-[#d6d6d6] dark:border-slate-700 flex items-center px-4 z-10 relative"
                style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                <style>{FADE_IN}</style>

                {/* ── LEFT ─ Identity ───────────────────────────────── */}
                <div className="flex items-center gap-1.5 flex-shrink-0 w-[240px]">
                    <button type="button" onClick={onBack}
                        className="inline-flex items-center gap-1 px-2 py-1.5 hover:bg-[#e1dfdd] dark:hover:bg-slate-700 rounded text-[#605e5c] dark:text-slate-400 transition-colors text-sm font-medium"
                        title="Back">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <div className="w-px h-4 bg-[#d6d6d6] dark:bg-slate-600 mx-0.5" />
                    {isEditingTitle ? (
                        <input type="text"
                            className="text-sm font-semibold text-[#252423] dark:text-slate-100 border-b border-[#118DF0] focus:outline-none bg-transparent min-w-[140px]"
                            value={tempTitle} autoFocus
                            onChange={e => setTempTitle(e.target.value)}
                            onBlur={handleTitleSubmit}
                            onKeyDown={e => e.key === 'Enter' && handleTitleSubmit()} />
                    ) : (
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className="select-none text-[#252423] dark:text-slate-100 hover:text-[#118DF0] transition-colors text-sm font-semibold cursor-pointer truncate max-w-[120px]"
                                onClick={() => setIsEditingTitle(true)}
                                style={{ color: titleStyle?.color, fontWeight: titleStyle?.bold ? 'bold' : 600, fontStyle: titleStyle?.italic ? 'italic' : 'normal', textDecoration: titleStyle?.underline ? 'underline' : 'none' }}>
                                {title}
                            </span>
                            {activeVersion ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-400 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-300 leading-none flex-shrink-0 whitespace-nowrap">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />{activeVersion}
                                </span>
                            ) : (
                                <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-600 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-400 leading-none flex-shrink-0 whitespace-nowrap">Draft</span>
                            )}
                            <button type="button" onClick={onHeaderSettings}
                                className="inline-flex items-center justify-center w-6 h-6 rounded text-[#a19f9d] hover:bg-[#e1dfdd] dark:hover:bg-slate-700 hover:text-[#605e5c] transition-colors flex-shrink-0">
                                <Settings className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>

                {/* ── CENTER ─ Toolbar (absolute centered) ─────────── */}
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-0.5">

                    {/* DATA dropdown */}
                    <button id="dashboard-data-preview-btn" type="button" onClick={() => setIsDataOpen(true)}
                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-md transition-all h-8 bg-transparent hover:bg-slate-200 dark:hover:bg-slate-700"
                        title="Data Model">
                        <Database className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300" />
                        <span className="text-slate-700 dark:text-slate-300">Data</span>
                        {dataModel && (
                            <span className="text-slate-500 dark:text-slate-400 font-medium ml-0.5 truncate max-w-[150px]">
                                {dataModel.name}
                            </span>
                        )}
                        <ChevronDown className="w-3.5 h-3.5 ml-0.5 text-slate-400" />
                    </button>

                    <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />

                    {/* TEMPLATES button */}
                    <button id="widget-toolbox-templates" type="button" onClick={() => setIsTemplatesOpen(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all h-8 text-[#252423] dark:text-slate-300 hover:bg-[#e1dfdd] dark:hover:bg-slate-700"
                        title="Browse templates">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                        <span>Templates</span>
                    </button>

                    {/* THEME button */}
                    <button id="dashboard-btn-theme" type="button" onClick={() => setIsThemePanelOpen(v => !v)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all h-8 ${
                            isThemePanelOpen
                                ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-300'
                                : 'text-[#252423] dark:text-slate-300 hover:bg-[#e1dfdd] dark:hover:bg-slate-700'
                        }`}
                        title="Dashboard theme">
                        <Palette className="w-3.5 h-3.5 text-purple-500" />
                        <span>Theme</span>
                    </button>

                    <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />

                    {/* Free / Auto Layout */}
                    <HeaderButton
                        icon={isCompact ? AlignJustify : LayoutTemplate}
                        label={isCompact ? 'Auto Layout' : 'Free Layout'}
                        onClick={() => { const nv = !isCompact; setIsCompact(nv); onToggleCompact?.(nv); }}
                        title={isCompact ? 'Switch to Free Placement' : 'Switch to Auto-Stacking'}
                        className={isCompact ? 'text-blue-600 bg-blue-50' : ''}
                    />

                    <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />

                    {/* New Measure */}
                    <HeaderButton icon={FunctionSquare} label="New Measure" variant="secondary"
                        onClick={() => onAction?.('newMeasure')} />
                </div>

                {/* ── RIGHT ─ Actions ───────────────────────────────── */}
                <div className="flex items-center gap-0.5 ml-auto flex-shrink-0">

                    {/* Versions */}
                    {!isNewDashboard && (
                        <>
                            <button onClick={() => onAction?.('versionHistory')}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-md border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 dark:bg-teal-900/10 dark:border-teal-800 dark:text-teal-300 dark:hover:bg-teal-900/20 transition-colors h-8"
                                title="Version history">
                                <History className="w-3.5 h-3.5" /> Versions
                            </button>
                            <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />
                        </>
                    )}

                    <span id="dashboard-btn-save"><HeaderButton icon={isSaving ? Loader2 : Save} label="Save" onClick={onSave} disabled={isSaving} className="min-w-[60px]" /></span>
                    <button id="dashboard-btn-publish" onClick={onPublish}
                        disabled={isNewDashboard}
                        title={isNewDashboard ? "Save dashboard to publish" : "Publish"}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors h-8 ml-1 shadow-sm ${isNewDashboard ? 'bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed' : 'bg-[#118DF0] hover:bg-[#0070c8] text-white'}`}>
                        <UploadCloud className="w-3.5 h-3.5" /> Publish
                    </button>
                    {!isNewDashboard && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    id="dashboard-btn-share"
                                    type="button"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all select-none h-8 bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700"
                                    title="Share and export options"
                                >
                                    <Share2 className="w-3.5 h-3.5" />
                                    <span>Share</span>
                                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                        <Download className="w-3.5 h-3.5 mr-2" />
                                        Export as PDF
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent className="w-56">
                                        <DropdownMenuItem onClick={() => onAction?.('export-pdf-snapshot')}>
                                            Snapshot (Frontend)
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => onAction?.('export-pdf-full-report')}>
                                            Full Report (Backend)
                                        </DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuItem onClick={() => onAction?.('export-csv-zip')}>
                                    Export as CSV (ZIP)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onAction?.('export-excel-zip')}>
                                    Export as Excel (ZIP)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onAction?.('export-json')}>
                                    Export as JSON Template
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => onAction?.('share-url')}>
                                    Embed / Publish URL
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    <div className="flex items-center gap-0.5 ml-1 pl-2 border-l border-[#d6d6d6] dark:border-slate-600">
                        <button onClick={() => onToggleSidebar(!isSidebarOpen)}
                            className="p-1.5 rounded transition-colors hover:bg-[#e1dfdd] dark:hover:bg-slate-700 text-[#605e5c]"
                            title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}>
                            {isSidebarOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                        </button>
                        <button onClick={() => onAction?.('fullscreen')}
                            className="p-1.5 rounded transition-colors hover:bg-[#e1dfdd] dark:hover:bg-slate-700 text-[#605e5c]"
                            title="Toggle fullscreen">
                            {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>

            {isTemplatesOpen && (
                <TemplatesModal
                    onClose={() => setIsTemplatesOpen(false)}
                    onApply={(tpl: any) => onTemplateApply?.(tpl)}
                    existingWidgetCount={widgetCount}
                />
            )}
            {isThemePanelOpen && (
                <ThemePanel
                    currentColorTheme={(currentColorTheme || 'light') as ColorThemeId}
                    currentLayout={(currentLayout || 'flat') as LayoutId}
                    onColorThemeChange={(id: ColorThemeId) => { onColorThemeChange?.(id); }}
                    onLayoutChange={(id: LayoutId) => { onLayoutChange?.(id); }}
                    onClose={() => setIsThemePanelOpen(false)}
                />
            )}
        <DataModelSelectionModal isOpen={isDataOpen} onClose={() => setIsDataOpen(false)} currentModel={dataModel} onConfirm={(m) => { onDataModelChange?.(m); setIsDataOpen(false); }} />
        </>
    );
};
